import { DatePipe } from '@angular/common';
import { Shift } from '../interfaces';
import { ProjectWithShifts } from '../components/calendar/calendar.component';
import { DateHelper } from '../helpers';
import { ProjectModel, UserModel } from '../models';

export class ResourceUtil {
	private static todayCache: Date | null = null;

	public static isToday(date: Date): boolean {
		if (!ResourceUtil.todayCache || ResourceUtil.isNewDay()) {
			ResourceUtil.todayCache = new Date();
		}

		return (
			date.getDate() === ResourceUtil.todayCache.getDate() &&
			date.getMonth() === ResourceUtil.todayCache.getMonth() &&
			date.getFullYear() === ResourceUtil.todayCache.getFullYear()
		);
	}

	// Helper method to check if the cached date is from a previous day
	private static isNewDay(): boolean {
		const now = new Date();
		return (
			ResourceUtil.todayCache!.getDate() !== now.getDate() ||
			ResourceUtil.todayCache!.getMonth() !== now.getMonth() ||
			ResourceUtil.todayCache!.getFullYear() !== now.getFullYear()
		);
	}

	public static formatDate(date: Date): string {
		const d = new Date(date);
		return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}

	public static getStartOfWeek(date: Date, firstDayOfTheWeek: number = 1): Date {
		const copy = new Date(date);
		// Calculate how many days to go back to reach the firstDayOfTheWeek
		const currentDayOfWeek = copy.getDay();
		let daysToSubtract = (currentDayOfWeek - firstDayOfTheWeek + 7) % 7;
		// Go back to the start of the week
		copy.setDate(copy.getDate() - daysToSubtract);
		return copy;
	}

	public static getEndOfWeek(date: Date, firstDayOfTheWeek: number = 1) {
		const startOfWeek = this.getStartOfWeek(date, firstDayOfTheWeek);
		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(startOfWeek.getDate() + 6);
		return endOfWeek;
	}

	public static getStartOfMonth(date: Date, firstDayOfTheWeek: number = 1): Date {
		const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
		startOfMonth.setHours(0, 0, 0, 0);
		// Get the start of the week that contains the first day of the month
		return this.getStartOfWeek(startOfMonth, firstDayOfTheWeek);
	}

	public static getEndOfMonth(date: Date, firstDayOfTheWeek: number = 1): Date {
		// Get the last day of the month
		const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
		endOfMonth.setHours(23, 59, 59, 999);
		// Get the end of the week that contains the last day of the month
		return this.getEndOfWeek(endOfMonth, firstDayOfTheWeek);
	}

	public static getCurrentWeek(datePipe: DatePipe, date: Date, us: boolean, firstDayOfTheWeek: number = 1): string {
		const startOfWeek = this.getStartOfWeek(date, firstDayOfTheWeek);
		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(endOfWeek.getDate() + 6);
		const dateFormat = us ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
		return datePipe.transform(startOfWeek, dateFormat) + ' - ' + datePipe.transform(endOfWeek, dateFormat);
	}

	public static daysOfWeek(startDate: Date, firstDayOfTheWeek: number = 1): Date[] {
		const startOfWeek = this.getStartOfWeek(startDate, firstDayOfTheWeek);

		const week = [];
		for (let i = 0; i < 7; i++) {
			const day = new Date(startOfWeek);
			day.setDate(startOfWeek.getDate() + i);
			week.push(day);
		}
		return week;
	}

	public static convertToMinutes(date: Date): number {
		return date.getHours() * 60 + date.getMinutes();
	}

	public static shiftDuration(shift: Shift, actual = false): number {
		let start = new Date(shift.startTime);
		let end = new Date(shift.endTime);

		// If the shift has, and we want the actual hours
		if (actual && shift.actualStartTime.length > 0 && shift.actualEndTime.length > 0) {
			start = new Date(shift?.actualStartTime[shift?.actualStartTime?.length - 1].date);
			end = new Date(shift?.actualEndTime[shift?.actualEndTime?.length - 1].date);
		}

		const breakMillis = shift.breakMinutes * 60 * 1000;
		const durationMillis = end.getTime() - start.getTime() - breakMillis;
		const durationHours = durationMillis / (1000 * 60 * 60);
		return durationHours;
	}

	public static isEmployeeAvailableForShift(shiftStartTime: number, shiftEndTime: number, availableFrom: number, availableTo: number) {
		//handle overnight shifts
		if (shiftEndTime <= shiftStartTime) {
			shiftEndTime += 1440; // add 24 hours in minutes
		}

		//handle overnight availability
		if (availableTo <= availableFrom) {
			availableTo += 1440; // add 24 hours in minutes
		}

		const isAvailable = availableFrom <= shiftStartTime && availableTo >= shiftEndTime;

		return {
			status: isAvailable,
			message: isAvailable
				? 'Employee Available'
				: `Employee is only available from ${DateHelper.serverTimeToDisplayTime(availableFrom)} and until ${DateHelper.serverTimeToDisplayTime(availableTo)} on this day`,
		};
	}

	public static isOvernightShift(shift: Shift): boolean {
		return shift.startTime.getDay() !== shift.endTime.getDay();
	}

	public static isOvertimeShift(shift: Shift, operatingHours): boolean {
		const shiftStartMinutes = this.convertToMinutes(new Date(shift.startTime));
		let shiftEndMinutes = this.convertToMinutes(new Date(shift.endTime));

		// handle overnight shifts
		if (shiftEndMinutes < shiftStartMinutes) {
			shiftEndMinutes += 24 * 60;
		}

		// check if shift is outside the project's operating hours
		if (operatingHours) {
			const operatingHoursStart = operatingHours.openingTime;
			let operatingHoursEnd = operatingHours.closingTime;

			// handle overnight operating hours for project
			if (operatingHoursEnd < operatingHoursStart) {
				operatingHoursEnd += 24 * 60;
			}

			if (shiftStartMinutes < operatingHoursStart || shiftEndMinutes > operatingHoursEnd) {
				return true;
			}
		}

		// check if the shift is longer than 8 hours
		return (shiftEndMinutes - shiftStartMinutes - shift.breakMinutes) / 60 > 8;
	}

	// Check if the shift is less than or equal to the operating hours
	public static isLessThanOrEqualShift(shift: Shift, operatingHours): boolean {
		const shiftStartMinutes = this.convertToMinutes(new Date(shift.startTime));
		const shiftEndMinutes = this.convertToMinutes(new Date(shift.endTime));

		if (operatingHours) {
			const operatingHoursStart = operatingHours.openingTime;
			const operatingHoursEnd = operatingHours.closingTime;
			const lessThan = shiftStartMinutes >= operatingHoursStart && shiftEndMinutes <= operatingHoursEnd;
			const equal = shiftStartMinutes === operatingHoursStart && shiftEndMinutes === operatingHoursEnd;
			return lessThan || equal;
		}

		return false;
	}

	public static shiftIsOverlapping(
		shifts: Shift[],
		projects: ProjectWithShifts[] | ProjectModel[],
		workers: UserModel[],
		userId: string,
		startTime: Date,
		endTime: Date,
		ignoreShiftId?: string,
	) {
		const overlappingShift = shifts.find(
			shift =>
				!!shift.user &&
				shift.user === userId &&
				(!ignoreShiftId || shift.id !== ignoreShiftId) &&
				new Date(shift.startTime) < endTime &&
				startTime < new Date(shift.endTime),
		);

		if (overlappingShift) {
			const project = projects.find(p => p.id === overlappingShift.project);
			const user = workers.find(user => user.id === overlappingShift.user);
			if (project) {
				return {
					result: true,
					message: `${user.firstName} ${user.lastName} is already scheduled on project ${project.name} during this time.`,
				};
			}

			return {
				result: true,
				message: `${user.firstName} ${user.lastName} is already scheduled during this time.`,
			};
		}

		return {
			result: false,
		};
	}

	public static formatSiteAddress(project: ProjectWithShifts): string {
		if (!project?.address) {
			return 'Address Not Available';
		}

		const { addressLine1, addressLine2, addressLine3, city, state, postCode } = project.address;

		const address = [addressLine1, addressLine2, addressLine3, city, state?.name, postCode];

		return address.filter(Boolean).join(', ');
	}

	public static getTotalWeeklyDuration(shifts: Shift[]): number {
		let totalDuration = 0;
		shifts.forEach(shift => {
			const start = new Date(shift.startTime);
			const end = new Date(shift.endTime);
			const breakMillis = shift.breakMinutes * 60 * 1000;
			const durationMillis = end.getTime() - start.getTime() - breakMillis;
			totalDuration += durationMillis / (1000 * 60 * 60); // Convert to hours
		});
		return totalDuration;
	}

	/**
	 * Returns true if a shift can be clocked into:
	 * - A user is assigned and the shift is published and not marked as worked
	 * - Current time is within 12 hours before shift start and not more than 12 hours after shift end
	 * - Has not already been clocked in
	 */
	public static canClockIn(shift: Shift): boolean {
		if (!shift) {
			return false;
		}

		if (!shift.user || !shift.isPublished || shift.worked) {
			return false;
		}

		const now = new Date();
		const twelveHours = 12 * 60 * 60 * 1000;
		const shiftStart = new Date(shift.startTime);
		const withinWindow = shiftStart.getTime() - now.getTime() <= twelveHours;
		const shiftEnd = new Date(shift.endTime);
		const notTooLate = now.getTime() - shiftEnd.getTime() <= twelveHours;
		const notClockedIn = !shift.actualStartTime || shift.actualStartTime.length === 0;

		return withinWindow && notTooLate && notClockedIn;
	}

	/**
	 * Returns true if a shift can be clocked out of:
	 * - Has clocked in
	 * - Current time is not more than 12 hours after shift end
	 * - Has not already been clocked out by a higher-level user
	 * - Has not already been clocked out
	 */
	public static canClockOut(shift: Shift): boolean {
		if (!shift) {
			return false;
		}

		const now = new Date();
		const twelveHours = 12 * 60 * 60 * 1000;
		const shiftEnd = new Date(shift.endTime);
		const withinWindow = now.getTime() - shiftEnd.getTime() <= twelveHours;
		const hasClockedIn = !!shift.actualStartTime && shift.actualStartTime.length > 0;

		if (!hasClockedIn || !withinWindow) {
			return false;
		}

		if (shift.actualEndTime && shift.actualEndTime.length > 0) {
			const lastEntry = shift.actualEndTime[shift.actualEndTime.length - 1];
			if (lastEntry.editedBy && lastEntry.editedBy !== shift.user) {
				return false;
			}
		}

		return !shift.actualEndTime || shift.actualEndTime.length === 0;
	}
}
